import { CENTRAL_PAYEMENT_MODULE, CENTRAL_PAYMENT_MARGIN_MODULE } from './../constant/centralPaymentConstant';
import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { applyColumnResize } from '../../utils/list/utils/columnResize';
import type { ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { cloneDeep } from "lodash";
import { OrganisationContact } from "../../organisation/objects/organisationContact";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_CENTRAL_PAYEMENT, EMPTY_CENTRAL_PAYEMENT_LIST_CONFIGURATION, EMPTY_CENTRAL_PAYEMENT_LIST_CRITERIA, EMPTY_CENTRAL_PAYMENT_MARGIN_LIST_CONFIGURATION, EMPTY_CENTRAL_PAYMENT_MARGIN_LIST_CRITERIA } from "../constant/centralPaymentConstant";
import { CentralPayment } from "../objects/centralPayment";
import { CentralPaymentListCriteria } from "../objects/centralPaymentListCriteria";
import { CentralPaymentState } from "../objects/centralPaymentState";
import { setCentralPaymentPreference } from "../utils/setCentralPaymentPreference";
import { Margin } from "../../margin/objects/margin";
import { setMarginPreference } from "../utils/setMarginPreference";
import { MarginListCriteria } from "../objects/marginListCriteria";
import { Column } from "../objects/column";


const initialState: CentralPaymentState = {
    central_payment_list: [],
    central_payment_list_configuration: EMPTY_CENTRAL_PAYEMENT_LIST_CONFIGURATION,
    central_payment_list_loading: false,
    central_payment_list_count: 0,
    central_payment_list_criteria: EMPTY_CENTRAL_PAYEMENT_LIST_CRITERIA,
    central_payment: EMPTY_CENTRAL_PAYEMENT,
    central_payment_contacts: [],
    central_payment_contacts_loading: false,
    central_payment_contact: null,
    margins: [],
    margins_configuration: EMPTY_CENTRAL_PAYMENT_MARGIN_LIST_CONFIGURATION,
    margin: null,
    margins_loading: false,
    margins_count: 0,
    margins_criteria: EMPTY_CENTRAL_PAYMENT_MARGIN_LIST_CRITERIA,
    suspended_margin: null,
    first_preference_loaded: false
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');


const setSuspendedMarginReducer = (state: CentralPaymentState, action: PayloadAction<Margin | null>): CentralPaymentState => ({
    ...state, suspended_margin: action.payload
});

const addMarginsConfigurationPreferenceReducer = (state: CentralPaymentState, action: PayloadAction<Preferences[]>): CentralPaymentState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: [...state.margins_configuration.preferences, ...action.payload] }
});

const setMarginsConfigurationPreferenceReducer = (state: CentralPaymentState, action: PayloadAction<Preferences[]>): CentralPaymentState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: action.payload }
});


const moveDownColumnMarginsReducer = (state: CentralPaymentState, action: PayloadAction<number>): CentralPaymentState => {
    const columns = cloneDeep([...state.margins_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload + 1] };
    columns[action.payload + 1] = columns[action.payload];
    columns[action.payload + 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, margins_configuration: { ...state.margins_configuration, columns } };
};


const moveUpColumnMarginsReducer = (state: CentralPaymentState, action: PayloadAction<number>): CentralPaymentState => {
    const columns = cloneDeep([...state.margins_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload - 1] };
    columns[action.payload - 1] = columns[action.payload];
    columns[action.payload - 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, margins_configuration: { ...state.margins_configuration, columns } };
};

const toggleColumnMarginsReducer = (state: CentralPaymentState, action: PayloadAction<number>): CentralPaymentState => {
    const columns = cloneDeep([...state.margins_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, margins_configuration: { ...state.margins_configuration, columns } };
};

const setNetworkMarginsReducer = (state: CentralPaymentState, action: PayloadAction<Margin[]>): CentralPaymentState => ({
    ...state, margins: action.payload
});
const updateMarginsCriteriaReducer = (state: CentralPaymentState, action: PayloadAction<MarginListCriteria>): CentralPaymentState => ({
    ...state, margins_criteria: action.payload
});
const updateMarginReducer = (state: CentralPaymentState, action: PayloadAction<Margin | null>): CentralPaymentState => ({
    ...state, margin: action.payload
});
const updateMarginsLoadingReducer = (state: CentralPaymentState, action: PayloadAction<boolean>): CentralPaymentState => ({
    ...state, margins_loading: action.payload
});
const updateMarginsCountReducer = (state: CentralPaymentState, action: PayloadAction<number>): CentralPaymentState => ({
    ...state, margins_count: action.payload
});

const addCentralPaymentListConfigurationPreferenceReducer = (state: CentralPaymentState, action: PayloadAction<Preferences[]>): CentralPaymentState => ({
    ...state, central_payment_list_configuration: { ...state.central_payment_list_configuration, preferences: [...state.central_payment_list_configuration.preferences, ...action.payload] }
});

const setCentralPaymentListConfigurationPreferenceReducer = (state: CentralPaymentState, action: PayloadAction<Preferences[]>): CentralPaymentState => ({
    ...state, central_payment_list_configuration: { ...state.central_payment_list_configuration, preferences: action.payload }
});

const addCentralPaymentContactReducer = (state: CentralPaymentState, action: PayloadAction<OrganisationContact>): CentralPaymentState => ({
    ...state, central_payment_contacts: [action.payload, ...state.central_payment_contacts]
});

const updateCentralPaymentContactReducer = (state: CentralPaymentState, action: PayloadAction<OrganisationContact>): CentralPaymentState => ({
    ...state, central_payment_contacts: state.central_payment_contacts.map((contact) => contact.id === action.payload.id ? action.payload : contact)
});
const updateCentralPaymentContactsReducer = (state: CentralPaymentState, action: PayloadAction<OrganisationContact[]>): CentralPaymentState => ({
    ...state, central_payment_contacts: action.payload
});

const deleteCentralPaymentContactReducer = (state: CentralPaymentState, action: PayloadAction<number>): CentralPaymentState => ({
    ...state, central_payment_contacts: state.central_payment_contacts.filter((contact) => contact.id !== action.payload)
});

const setPreferenceReducer = (state: CentralPaymentState, action: PayloadAction<Preferences[]>): CentralPaymentState => {
    let main_preference = {};
    if(action.payload.find(value => value.module === CENTRAL_PAYEMENT_MODULE)) {
        const { preferences, columns, order_by, order } = setCentralPaymentPreference(action.payload, state.central_payment_list_configuration.columns);
        main_preference = {
            central_payment_list_configuration: { ...state.central_payment_list_configuration, preferences, columns },
            central_payment_list_criteria: { ...state.central_payment_list_criteria, order, order_by },
        };
    }
    let margin_pref = {};
    if(action.payload.find(value => value.module === CENTRAL_PAYMENT_MARGIN_MODULE)) {
        const { preferences, columns, order_by, order } = setMarginPreference(action.payload, state.margins_configuration.columns);
        margin_pref = {
            margins_configuration: { ...state.margins_configuration, preferences: preferences, columns: columns },
            margins_criteria: { ...state.margins_criteria, order: order, order_by: order_by }
        };
    }
    return {
        ...state,
        ...main_preference,
        ...margin_pref,
        first_preference_loaded: true
    };
};

const toggleColumnReducer = (state: CentralPaymentState, action: PayloadAction<number>): CentralPaymentState => {
    const columns = cloneDeep([...state.central_payment_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, central_payment_list_configuration: { ...state.central_payment_list_configuration, columns } };
};

const updateCentralPaymentListReducer = (state: CentralPaymentState, action: PayloadAction<CentralPayment[]>): CentralPaymentState => ({
    ...state, central_payment_list: action.payload
});
const updateCentralPaymentListCriteriaReducer = (state: CentralPaymentState, action: PayloadAction<CentralPaymentListCriteria>): CentralPaymentState => ({
    ...state, central_payment_list_criteria: action.payload
});
const updateCentralPaymentReducer = (state: CentralPaymentState, action: PayloadAction<CentralPayment>): CentralPaymentState => ({
    ...state, central_payment: action.payload
});
const updateCentralPaymentListLoadingReducer = (state: CentralPaymentState, action: PayloadAction<boolean>): CentralPaymentState => ({
    ...state, central_payment_list_loading: action.payload
});
const updateCentralPaymentListCountReducer = (state: CentralPaymentState, action: PayloadAction<number>): CentralPaymentState => ({
    ...state, central_payment_list_count: action.payload
});

const setColumnsReducer = (state: CentralPaymentState, action: PayloadAction<Column[]>): CentralPaymentState => ({
    ...state, central_payment_list_configuration: { ...state.central_payment_list_configuration, columns: action.payload },
});

const setMarginColumnsReducer = (state: CentralPaymentState, action: PayloadAction<Column[]>): CentralPaymentState => ({
    ...state, margins_configuration: { ...state.margins_configuration, columns: action.payload },
});

export const centralPayment = createSlice({
    name: 'central_payment',
    initialState,
    reducers: {
        setSuspendedMargin: setSuspendedMarginReducer,
        addMarginsConfigurationPreference: addMarginsConfigurationPreferenceReducer,
        setMarginsConfigurationPreference: setMarginsConfigurationPreferenceReducer,
        moveDownColumnMargins: moveDownColumnMarginsReducer,
        moveUpColumnMargins: moveUpColumnMarginsReducer,
        toggleColumnMargins: toggleColumnMarginsReducer,
        setCentralPaymentMargins: setNetworkMarginsReducer,
        updateMarginsCriteria: updateMarginsCriteriaReducer,
        updateMargin: updateMarginReducer,
        updateMarginsLoading: updateMarginsLoadingReducer,
        updateMarginsCount: updateMarginsCountReducer,
        addCentralPaymentContact: addCentralPaymentContactReducer,
        updateCentralPaymentContact: updateCentralPaymentContactReducer,
        updateCentralPaymentContacts: updateCentralPaymentContactsReducer,
        deleteCentralPaymentContact: deleteCentralPaymentContactReducer,
        addCentralPaymentListConfigurationPreference: addCentralPaymentListConfigurationPreferenceReducer,
        setCentralPaymentListConfigurationPreference: setCentralPaymentListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        updateCentralPaymentList: updateCentralPaymentListReducer,
        updateCentralPaymentListCriteria: updateCentralPaymentListCriteriaReducer,
        updateCentralPayment: updateCentralPaymentReducer,
        updateCentralPaymentListLoading: updateCentralPaymentListLoadingReducer,
        updateCentralPaymentListCount: updateCentralPaymentListCountReducer,
        setColumns: setColumnsReducer,
        setMarginColumns: setMarginColumnsReducer,
        resizeColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.central_payment_list_configuration.columns = applyColumnResize(state.central_payment_list_configuration.columns, action.payload);
        },
        resizeMarginsColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.margins_configuration.columns = applyColumnResize(state.margins_configuration.columns, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setSuspendedMargin,
    addMarginsConfigurationPreference,
    setMarginsConfigurationPreference,
    setColumns,
    toggleColumnMargins,
    setCentralPaymentMargins,
    updateMarginsCriteria,
    updateMargin,
    updateMarginsLoading,
    updateMarginsCount,
    addCentralPaymentContact,
    updateCentralPaymentContact,
    updateCentralPaymentContacts,
    deleteCentralPaymentContact,
    addCentralPaymentListConfigurationPreference,
    setCentralPaymentListConfigurationPreference,
    moveUpColumnMargins,
    moveDownColumnMargins,
    toggleColumn,
    updateCentralPaymentList,
    updateCentralPaymentListCriteria,
    updateCentralPayment,
    updateCentralPaymentListLoading,
    updateCentralPaymentListCount,
    setMarginColumns,
    resizeColumn,
    resizeMarginsColumn
} = centralPayment.actions;